import type { BeatView, Comic, PlayerRef, RevealView, RoomState } from '@panels/shared';
import { TITLE_MAX_CHARS } from '@panels/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ComicStrip } from '../components/ComicStrip.js';
import { ErrorNote, Screen, Spinner, StatusDot, type Status } from '../components/ui.js';
import { ApiError, getComic, revealAction, type ClientRevealAction } from '../lib/api.js';
import { serverClock, serverToPerf } from '../lib/clock.js';
import { useNow } from '../lib/usePoll.js';

/**
 * The co-viewed reveal (Round 3). One person drives; everyone else's screen
 * follows, and every replay starts at the same instant on everyone's screen. An
 * untitled comic gets a naming beat once its replay ends: anyone can suggest a
 * name, and the creator picks one, types their own, or leaves it unnamed.
 *
 * Solo browsing (co-viewing off) is the lobby and the comic page, unchanged.
 */

interface Props {
  roomId: string;
  state: RoomState;
  apply: (state: RoomState) => void;
}

type Sheet = null | 'menu' | 'handOff' | 'forceSkip';

export function Reveal({ roomId, state, apply }: Props) {
  const reveal = state.reveal!;
  const comics = useComicCache(reveal);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const toast = useDriverToast(reveal);
  // Which replay (comic + scheduled start) has finished on this screen.
  const [finishedKey, setFinishedKey] = useState<string | null>(null);
  const playKey = `${reveal.sheetId}:${reveal.startAtMs}`;
  const replayOver = reveal.replaySkipped || finishedKey === playKey;

  const act = async (action: ClientRevealAction) => {
    setBusy(true);
    setError(null);
    try {
      apply(await revealAction(roomId, action));
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not do that. Check your connection.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const { index, order, sheetId } = reveal;
  const canMenu = reveal.youDrive || reveal.youHost;
  const title = index < 0 ? `Round ${state.round?.roundNumber ?? ''} comics` : `Comic ${index + 1} of ${order.length}`;

  return (
    <Screen
      title={title}
      back="/"
      wide
      action={
        canMenu ? (
          <button type="button" className="icon-button" aria-label="Reveal options" onClick={() => setSheet('menu')}>
            ⋯
          </button>
        ) : undefined
      }
    >
      <DriverBanner reveal={reveal} />
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {reveal.takeOver.open && !reveal.youDrive ? (
        <TakeOver
          reveal={reveal}
          answeredAtMs={state.clock?.sentMs ?? serverClock.serverNow()}
          busy={busy}
          onTakeOver={() => void act({ type: 'takeOver' })}
        />
      ) : null}

      {index < 0 || !sheetId ? (
        <Intro reveal={reveal} busy={busy} onStart={() => void act({ type: 'next', at: null })} />
      ) : (
        <ComicStage
          key={sheetId}
          reveal={reveal}
          sheetId={sheetId}
          comic={comics[sheetId] ?? null}
          liveTitle={state.latestRoundSheets.find((s) => s.sheetId === sheetId)?.title ?? null}
          playKey={playKey}
          replayOver={replayOver}
          onReplayOver={() => setFinishedKey(playKey)}
          busy={busy}
          act={act}
        />
      )}

      {reveal.youDrive && sheetId ? <DriverBar reveal={reveal} replayOver={replayOver} busy={busy} act={act} /> : null}

      {sheet ? (
        <div className="sheet-backdrop" onClick={() => setSheet(null)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            {sheet === 'menu' ? (
              <RevealMenu reveal={reveal} busy={busy} onPick={setSheet} act={act} close={() => setSheet(null)} />
            ) : sheet === 'handOff' ? (
              <HandOff reveal={reveal} youId={state.you.id} busy={busy} act={act} close={() => setSheet(null)} />
            ) : (
              <ForceSkip reveal={reveal} busy={busy} act={act} close={() => setSheet(null)} />
            )}
          </div>
        </div>
      ) : null}

      {toast ? <div className="reveal-toast">{toast}</div> : null}
    </Screen>
  );
}

/* ------------------------------------------------------------------ pieces */

function DriverBanner({ reveal }: { reveal: RevealView }) {
  if (reveal.youDrive) {
    return (
      <div className="reveal-banner is-driving">
        <span>You’re driving. Everyone sees what you see.</span>
      </div>
    );
  }
  return (
    <div className="reveal-banner">
      <span>
        Watching with <strong>{reveal.driver.displayName}</strong>
      </span>
    </div>
  );
}

/** The driver went quiet: anyone here can take the wheel. First tap wins. */
function TakeOver({
  reveal,
  answeredAtMs,
  busy,
  onTakeOver,
}: {
  reveal: RevealView;
  /** Server clock when this state was built. */
  answeredAtMs: number;
  busy: boolean;
  onTakeOver: () => void;
}) {
  useNow(true);
  const left = Math.max(0, answeredAtMs + (reveal.takeOver.soloInMs ?? 0) - serverClock.serverNow());
  return (
    <div className="card take-over">
      <p>
        <strong>{reveal.driver.displayName}</strong> seems to have stepped away.
      </p>
      <button type="button" className="button primary" onClick={onTakeOver} disabled={busy}>
        Take over
      </button>
      <p className="muted small">
        If nobody does, everyone browses on their own in <span className="mono">{formatSeconds(left)}</span>.
      </p>
    </div>
  );
}

function Intro({ reveal, busy, onStart }: { reveal: RevealView; busy: boolean; onStart: () => void }) {
  return (
    <section className="card reveal-intro">
      <h2>
        {reveal.order.length} {reveal.order.length === 1 ? 'comic' : 'comics'} to watch together
      </h2>
      <p className="muted">
        {reveal.youDrive
          ? 'Start the show once everyone’s here. Each comic replays for everyone at once.'
          : `${reveal.driver.displayName} will start the show.`}
      </p>
      <p className="small">
        <span className="muted">Here now: </span>
        {reveal.present.map((p) => p.displayName).join(', ') || 'just you'}
      </p>
      {reveal.youDrive ? (
        <button type="button" className="button primary" onClick={onStart} disabled={busy}>
          Start the show
        </button>
      ) : null}
    </section>
  );
}

function ComicStage({
  reveal,
  sheetId,
  comic,
  liveTitle,
  playKey,
  replayOver,
  onReplayOver,
  busy,
  act,
}: {
  reveal: RevealView;
  sheetId: string;
  comic: Comic | null;
  liveTitle: string | null;
  playKey: string;
  replayOver: boolean;
  onReplayOver: () => void;
  busy: boolean;
  act: (a: ClientRevealAction) => Promise<boolean>;
}) {
  // The shared start, converted once per move to this device's animation clock.
  const startAt = useMemo(() => serverToPerf(reveal.startAtMs), [playKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const named = reveal.beat?.status === 'named' ? reveal.beat.title : null;
  const titleText = named ?? liveTitle ?? comic?.title ?? (comic ? `${comic.starter.displayName}'s comic` : '');

  return (
    <>
      <div className="comic-header">
        <div className="comic-title-row">
          <h2>{titleText}</h2>
        </div>
        {comic?.status === 'abandoned' ? <p className="muted small">Unfinished — someone dropped out mid-round.</p> : null}
      </div>

      {comic ? (
        <ComicStrip
          key={playKey}
          comic={comic}
          zoomable
          sync={{
            startAt,
            startAtServerMs: reveal.startAtMs,
            skipped: reveal.replaySkipped,
            onSkip: reveal.youDrive ? () => void act({ type: 'skipReplay', at: sheetId }) : undefined,
          }}
          onFinished={onReplayOver}
        />
      ) : (
        <Spinner label="Loading the comic…" />
      )}

      {reveal.beat && (replayOver || reveal.beat.status !== 'open') ? (
        <NamingBeat key={sheetId} beat={reveal.beat} busy={busy} act={act} />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------ naming beat */

function NamingBeat({ beat, busy, act }: { beat: BeatView; busy: boolean; act: (a: ClientRevealAction) => Promise<boolean> }) {
  if (beat.status === 'named') {
    return (
      <div className="announcement" role="status">
        <span className="muted small">This comic has been named</span>
        <strong className="announcement-title">{beat.title}</strong>
        {beat.credit ? <span className="small">suggested by {beat.credit.displayName}</span> : null}
      </div>
    );
  }
  if (beat.status === 'declined') {
    return <p className="muted small center">{beat.youCreate ? 'You left it' : `${beat.creator.displayName} left it`} unnamed.</p>;
  }
  if (beat.status === 'skipped') return <p className="muted small center">No name this time.</p>;
  return beat.youCreate ? <CreatorPicker beat={beat} busy={busy} act={act} /> : <SuggestBox beat={beat} busy={busy} act={act} />;
}

/** The creator: pick a suggestion, type their own, or leave it unnamed in one tap. */
function CreatorPicker({ beat, busy, act }: { beat: BeatView; busy: boolean; act: (a: ClientRevealAction) => Promise<boolean> }) {
  const [own, setOwn] = useState('');
  const sheetId = beat.sheetId;

  return (
    <section className="card beat-card">
      <h2>Name your comic</h2>
      <button type="button" className="button block-button" onClick={() => void act({ type: 'decline', sheetId })} disabled={busy}>
        Leave it unnamed
      </button>

      {beat.suggestions.length > 0 ? (
        <ul className="suggestions">
          {beat.suggestions.map((s) => (
            <li key={s.player.id}>
              <button
                type="button"
                className="suggestion"
                disabled={busy}
                onClick={() => void act({ type: 'name', sheetId, title: s.text, suggestedBy: s.player.id })}
              >
                <span className="suggestion-text">{s.text}</span>
                <span className="muted small">{s.player.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">Suggestions show up here as people send them. Tap one to use it.</p>
      )}

      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (own.trim()) void act({ type: 'name', sheetId, title: own });
        }}
      >
        <input value={own} onChange={(e) => setOwn(e.target.value)} placeholder="Or type your own" maxLength={TITLE_MAX_CHARS} />
        <button type="submit" className="button small" disabled={busy || !own.trim()}>
          Name it
        </button>
      </form>
    </section>
  );
}

/** Everyone else: an optional suggestion, dismissible with one tap, editable until the beat ends. */
function SuggestBox({ beat, busy, act }: { beat: BeatView; busy: boolean; act: (a: ClientRevealAction) => Promise<boolean> }) {
  const [dismissed, setDismissed] = useState(false);
  const [editing, setEditing] = useState(beat.yourSuggestion === null);
  const [text, setText] = useState(beat.yourSuggestion ?? '');
  const choosing = `${beat.creator.displayName} is choosing a name`;

  if (dismissed) {
    return (
      <p className="muted small center">
        {choosing}…{' '}
        <button type="button" className="link-button" onClick={() => setDismissed(false)}>
          Suggest one
        </button>
      </p>
    );
  }

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await act({ type: 'suggest', sheetId: beat.sheetId, text })) setEditing(false);
  };

  return (
    <section className="card beat-card">
      <div className="beat-head">
        <h2>Suggest a name</h2>
        <button type="button" className="icon-button" aria-label="No thanks" onClick={() => setDismissed(true)}>
          ✕
        </button>
      </div>
      <p className="muted small">
        {choosing}.{' '}
        {beat.suggestionCount > 0 ? `${beat.suggestionCount} ${beat.suggestionCount === 1 ? 'suggestion' : 'suggestions'} so far.` : ''}
      </p>
      {editing ? (
        <form className="inline-form" onSubmit={send}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="The Horse Incident" maxLength={TITLE_MAX_CHARS} autoFocus={beat.yourSuggestion !== null} />
          <button type="submit" className="button small" disabled={busy || (!text.trim() && beat.yourSuggestion === null)}>
            {beat.yourSuggestion === null ? 'Send' : text.trim() ? 'Update' : 'Withdraw'}
          </button>
        </form>
      ) : (
        <div className="your-suggestion">
          <span>
            You suggested <strong>“{beat.yourSuggestion}”</strong>
          </span>
          <button type="button" className="link-button" onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
      )}
    </section>
  );
}

/* ----------------------------------------------------------- driver tools */

function DriverBar({
  reveal,
  replayOver,
  busy,
  act,
}: {
  reveal: RevealView;
  replayOver: boolean;
  busy: boolean;
  act: (a: ClientRevealAction) => Promise<boolean>;
}) {
  const last = reveal.index >= reveal.order.length - 1;
  const { status, label } = replayOver ? driverStatus(reveal) : { status: 'idle' as Status, label: 'Playing…' };
  return (
    <div className="reveal-bar">
      <button
        type="button"
        className="button"
        onClick={() => void act({ type: 'back', at: reveal.sheetId })}
        disabled={busy || reveal.index <= 0}
      >
        ‹ Back
      </button>
      <StatusDot status={status} label={label} />
      <button
        type="button"
        className="button primary"
        onClick={() => void act({ type: 'next', at: reveal.sheetId })}
        disabled={busy || reveal.nextBlocked}
      >
        {last ? 'Finish' : 'Next ›'}
      </button>
    </div>
  );
}

/** The driver's at-a-glance status: amber while the naming beat holds the show, green once it's free. */
function driverStatus(reveal: RevealView): { status: Status; label: string } {
  const beat = reveal.beat;
  if (!beat) return { status: 'ready', label: 'Ready' };
  switch (beat.status) {
    case 'open':
      return { status: 'waiting', label: beat.youCreate ? 'Name it, or leave it unnamed' : `Waiting on ${beat.creator.displayName} to name it` };
    case 'named':
      return { status: 'ready', label: 'Named' };
    case 'declined':
      return { status: 'ready', label: 'Left unnamed' };
    case 'skipped':
      return { status: 'ready', label: 'Naming skipped' };
  }
}

function RevealMenu({
  reveal,
  busy,
  onPick,
  act,
  close,
}: {
  reveal: RevealView;
  busy: boolean;
  onPick: (s: Sheet) => void;
  act: (a: ClientRevealAction) => Promise<boolean>;
  close: () => void;
}) {
  const others = reveal.present.filter((p) => p.id !== reveal.driver.id);
  const beatOpen = reveal.beat?.status === 'open';
  return (
    <>
      <h2>Reveal options</h2>
      {reveal.youDrive ? (
        <button type="button" className="button" onClick={() => onPick('handOff')} disabled={others.length === 0}>
          {others.length === 0 ? 'Hand off control (nobody else is here)' : 'Hand off control…'}
        </button>
      ) : null}
      <button
        type="button"
        className="button"
        disabled={busy}
        onClick={async () => {
          if (await act({ type: 'turnOff' })) close();
        }}
      >
        Turn off co-viewing for everyone
      </button>
      {reveal.youDrive && beatOpen ? (
        <button type="button" className="button danger-outline" onClick={() => onPick('forceSkip')}>
          Skip naming this comic…
        </button>
      ) : null}
      <button type="button" className="link-button block" onClick={close}>
        Cancel
      </button>
    </>
  );
}

function HandOff({
  reveal,
  youId,
  busy,
  act,
  close,
}: {
  reveal: RevealView;
  youId: string;
  busy: boolean;
  act: (a: ClientRevealAction) => Promise<boolean>;
  close: () => void;
}) {
  const others = reveal.present.filter((p: PlayerRef) => p.id !== youId);
  return (
    <>
      <h2>Hand off control</h2>
      <p className="muted small">They drive from here. You can only get it back if they hand it back.</p>
      {others.map((p) => (
        <button
          key={p.id}
          type="button"
          className="button"
          disabled={busy}
          onClick={async () => {
            if (await act({ type: 'handOff', to: p.id })) close();
          }}
        >
          {p.displayName}
        </button>
      ))}
      <button type="button" className="link-button block" onClick={close}>
        Cancel
      </button>
    </>
  );
}

/** Emergency only, so it sits behind the menu and a confirm. */
function ForceSkip({
  reveal,
  busy,
  act,
  close,
}: {
  reveal: RevealView;
  busy: boolean;
  act: (a: ClientRevealAction) => Promise<boolean>;
  close: () => void;
}) {
  const creator = reveal.beat?.creator.displayName ?? 'The creator';
  return (
    <>
      <h2>Skip naming this comic?</h2>
      <p className="muted">
        {creator} hasn’t named it yet. It stays unnamed, and they can still name it later from the gallery.
      </p>
      <button
        type="button"
        className="button primary"
        disabled={busy || !reveal.sheetId}
        onClick={async () => {
          if (reveal.sheetId && (await act({ type: 'forceSkip', sheetId: reveal.sheetId }))) close();
        }}
      >
        Skip naming
      </button>
      <button type="button" className="button" onClick={close}>
        Keep waiting
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ hooks */

/** The current comic and its neighbours, fetched once each, so Next and Back start on time. */
function useComicCache(reveal: RevealView): Record<string, Comic> {
  const [comics, setComics] = useState<Record<string, Comic>>({});
  const inflight = useRef(new Set<string>());
  const { index, order } = reveal;

  useEffect(() => {
    const wanted = [order[Math.max(0, index)], order[index + 1], order[index - 1]].filter((id): id is string => !!id);
    for (const id of wanted) {
      if (comics[id] || inflight.current.has(id)) continue;
      inflight.current.add(id);
      getComic(id)
        .then((comic) => setComics((all) => ({ ...all, [id]: comic })))
        // A failed fetch is retried on the next poll, which re-runs this.
        .catch(() => undefined)
        .finally(() => inflight.current.delete(id));
    }
  }, [index, order, comics]);

  return comics;
}

/** "Rico is now driving." for everyone, whenever the driver changes. */
function useDriverToast(reveal: RevealView): string | null {
  const [toast, setToast] = useState<string | null>(null);
  const previous = useRef(reveal.driver.id);
  useEffect(() => {
    if (previous.current === reveal.driver.id) return;
    previous.current = reveal.driver.id;
    setToast(reveal.youDrive ? 'You’re driving now.' : `${reveal.driver.displayName} is now driving.`);
    const timer = window.setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [reveal.driver.id, reveal.driver.displayName, reveal.youDrive]);
  return toast;
}

function formatSeconds(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
